/*
Extraction of the 400 error shapes of POST/PATCH configs/.

The backend keys validation errors by field: `name` (per-owner duplicate)
and `parameters` (a LIST of "<loc>: <msg>" strings from the Pydantic
schema — the DRF handler wraps each as {message, code}). ApiError.fields
only carries the first error per field, so the full parameters list is
read from the raw body on ApiError.data.
*/

export type SaveFieldErrors = {
  name: string | null;
  parameters: string[];
};

type ErrorEntry = string | {message?: string};

const entryMessage = (entry: ErrorEntry): string | null => {
  if (typeof entry === 'string') {
    return entry;
  }
  return entry?.message ?? null;
};

const listMessages = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    const single = entryMessage(value as ErrorEntry);
    return single ? [single] : [];
  }
  return value
    .map((entry) => entryMessage(entry as ErrorEntry))
    .filter((message): message is string => message !== null);
};

export const extractSaveErrors = (error: unknown): SaveFieldErrors => {
  const apiError = error as {
    statusCode?: number;
    data?: Record<string, unknown>;
    fields?: Record<string, {message?: string} | string>;
  };
  if (apiError?.statusCode !== 400) {
    return {name: null, parameters: []};
  }
  const body = apiError.data ?? {};
  const nameMessages = listMessages(body.name);
  const parameterMessages = listMessages(body.parameters);
  // Fall back to the parsed fields when the raw body is unavailable.
  const fieldName = apiError.fields?.name;
  const fallbackName =
    typeof fieldName === 'string' ? fieldName : (fieldName?.message ?? null);
  const fieldParameters = apiError.fields?.parameters;
  const fallbackParameters =
    typeof fieldParameters === 'string'
      ? [fieldParameters]
      : fieldParameters?.message
        ? [fieldParameters.message]
        : [];
  return {
    name: nameMessages[0] ?? fallbackName,
    parameters:
      parameterMessages.length > 0 ? parameterMessages : fallbackParameters,
  };
};
